/**
 * AppRail — shared left sidebar used by /dashboard and /students.
 *
 * Self-contained: reads threads from localStorage, uses react-router for
 * navigation, and tracks collapsed state per-tab via localStorage. Drop it
 * at the root of any app page that wants the same left nav.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

/**
 * Shared store for the mobile rail drawer. AppTopBar's hamburger sets it;
 * the mobile <MobileRailDrawer /> mounted alongside <AppRail/> reads it.
 * Tiny custom store instead of context to keep the API of AppRail/AppTopBar
 * unchanged for every page that already uses them.
 */
const drawerStore = {
  open: false,
  listeners: new Set<() => void>(),
  setOpen(v: boolean) {
    this.open = v;
    this.listeners.forEach((fn) => fn());
  },
  subscribe(fn: () => void) {
    this.listeners.add(fn);
    return () => { this.listeners.delete(fn); };
  },
};

function useDrawerOpen() {
  const [open, setOpen] = useState(drawerStore.open);
  useEffect(() => drawerStore.subscribe(() => setOpen(drawerStore.open)), []);
  return open;
}

/** Toggle helpers — used by the AppTopBar hamburger and the drawer backdrop. */
export function openMobileRail() { drawerStore.setOpen(true); }
export function closeMobileRail() { drawerStore.setOpen(false); }
import {
  Plus, Users, Briefcase, Building2, ClipboardList,
  Sidebar, LogOut,
  Sparkles, Zap, BarChart3,
  X, Menu, ShieldCheck, Filter, LayoutDashboard, LifeBuoy, Gauge, Sun, Moon, Radio,
} from 'lucide-react';
import { useAuth } from '../../auth';
import { useTeamProfile } from '../../../shared/hooks/useTeamProfile';
import { useBranding } from '../../../shared/hooks/useBranding';
import { listThreads, refreshThreads, formatThreadTime, type ThreadMeta } from '../lib/threads';
import { streamingState, STREAMING_EVENT } from './dashboard/useChatEngine';
import { Pin, MessageSquare, Lock, Settings as SettingsIcon, HardDrive, ScrollText, ChevronDown, Handshake, Mic, ClipboardCheck, FileText } from 'lucide-react';
import { useDarkMode } from '../../../shared/hooks/useDarkMode';
import { FresherbotLogo } from '../../../shared/components/FresherbotLogo';

/** v1 hides the Drives module from rendering — set VITE_ENABLE_DRIVES=true
 *  in env to bring it back. The page-router enforces the same flag at the
 *  route level so deep-linked URLs 404 as well. */
const DRIVES_ENABLED =
  ((import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {}).VITE_ENABLE_DRIVES === 'true';

// White-label deployments (testmug / packetprep) build with VITE_WHITELABEL=true.
// Their TPO users get a slimmed "Institution Admin" rail — Students / Tests /
// Reports only, no AI chat — matching the partner-admin console style.
const IS_WHITELABEL =
  ((import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {}).VITE_WHITELABEL === 'true';

/** Format a share-expiry timestamp as a short human label (used by tooltips). */
function shareStatusLabel(expiresAt?: number | null): string {
  if (expiresAt == null) return 'Shared · untimed';
  const ms = expiresAt - Date.now();
  if (ms <= 0) return 'Shared · expired';
  const mins = Math.round(ms / 60_000);
  if (mins < 60) return `Shared · expires in ${mins}m`;
  if (mins < 24 * 60) return `Shared · expires in ${Math.round(mins / 60)}h`;
  return `Shared · expires ${new Date(expiresAt).toLocaleString()}`;
}

// The app palette. Every token is a CSS variable defined in
// styles/index.css for BOTH themes — see the "App palette tokens" block
// there. Keeping the literals out of JS is what makes dark mode work at
// all: ~97 files import this as APP_C and apply it through inline styles
// (~10k call sites), and a var() re-resolves when <html data-theme>
// flips, whereas a baked-in oklch string never would.
//
// The var() fallbacks below are the light values, so a page rendered
// before the theme is stamped (or with the stylesheet missing) still
// looks exactly as it did.
//
// accent/accentSoft additionally honour --brand-accent, set inline on
// <html> by useApplyBranding for white-label deployments — that still
// overrides the per-theme default, unchanged.
const C = {
  bg2:        'var(--fb-bg2, oklch(0.945 0.005 260))',
  surface:    'var(--fb-surface, #ffffff)',
  ink:        'var(--fb-ink, oklch(0.20 0.012 270))',
  ink2:       'var(--fb-ink2, oklch(0.42 0.010 270))',
  ink3:       'var(--fb-ink3, oklch(0.62 0.008 270))',
  ink4:       'var(--fb-ink4, oklch(0.78 0.006 270))',
  line:       'var(--fb-line, oklch(0.91 0.006 270))',
  line2:      'var(--fb-line2, oklch(0.94 0.005 270))',
  accent:     'var(--fb-accent, var(--brand-accent, oklch(0.56 0.19 264)))',
  /** Accent-coloured TEXT/icons. Use this for `color:`; use `accent` for
   *  `background:`. They diverge in dark — see index.css. */
  accentInk:  'var(--fb-accent-ink, var(--brand-accent, oklch(0.56 0.19 264)))',
  accentSoft: 'var(--fb-accent-soft, var(--brand-accent-soft, oklch(0.955 0.035 264)))',
  shadowSm:   'var(--fb-shadow-sm, 0 1px 2px oklch(0.2 0.01 270 / 0.04), 0 2px 6px -2px oklch(0.2 0.01 270 / 0.05))',

  // Status colours. Each is a triple: `X` = text/icon, `XSoft` = chip fill,
  // `XLine` = chip border. Use these instead of literal reds/greens/ambers —
  // a hardcoded `#b91c1c on #fef2f2` badge is unreadable in dark mode, which
  // is the whole reason these exist.
  danger:      'var(--fb-danger-ink, oklch(0.50 0.18 25))',
  dangerSoft:  'var(--fb-danger-soft, oklch(0.96 0.04 25))',
  dangerLine:  'var(--fb-danger-line, oklch(0.85 0.06 25))',
  success:     'var(--fb-success-ink, oklch(0.45 0.16 145))',
  successSoft: 'var(--fb-success-soft, oklch(0.95 0.06 145))',
  successLine: 'var(--fb-success-line, oklch(0.85 0.08 145))',
  warn:        'var(--fb-warn-ink, oklch(0.45 0.16 60))',
  warnSoft:    'var(--fb-warn-soft, oklch(0.97 0.04 60))',
  warnLine:    'var(--fb-warn-line, oklch(0.87 0.07 60))',
  info:        'var(--fb-info-ink, oklch(0.50 0.16 250))',
  infoSoft:    'var(--fb-info-soft, oklch(0.96 0.03 250))',
  infoLine:    'var(--fb-info-line, oklch(0.86 0.06 250))',
  /** Status FILLS — for `background:` with onAccent text. The plain
   *  danger/warn/success/info above are TEXT colours and go light in dark,
   *  so filling a button with them and writing white on top disappears. */
  dangerFill:  'var(--fb-danger-fill, oklch(0.55 0.20 25))',
  warnFill:    'var(--fb-warn-fill, oklch(0.55 0.15 60))',
  successFill: 'var(--fb-success-fill, oklch(0.50 0.15 155))',
  infoFill:    'var(--fb-info-fill, oklch(0.52 0.16 250))',
  /** Foreground for text/icons sitting ON an accent-filled surface. */
  onAccent:    'var(--fb-on-accent, #ffffff)',
  /** Sticky top bar — translucent over the page canvas. */
  topbar:      'var(--fb-topbar, oklch(0.960 0.004 260 / 0.8))',
  /** Inverted-emphasis button ("black button"). ALWAYS pair invert with
   *  invertInk — using C.ink as a background and white as the text breaks
   *  in dark, where ink is near-white. */
  invert:      'var(--fb-invert-bg, oklch(0.20 0.012 270))',
  invertInk:   'var(--fb-invert-ink, #ffffff)',
};
const MONO = '"JetBrains Mono", "Fira Code", ui-monospace, monospace';

export function AppRailItem({
  icon, label, collapsed, active, to, onClick,
}: {
  icon: React.ReactNode;
  label: string;
  collapsed: boolean;
  active?: boolean;
  to?: string;
  onClick?: () => void;
}) {
  const navigate = useNavigate();
  const handle = () => { if (onClick) onClick(); if (to) navigate(to); };
  const body = (
    <button
      onClick={handle}
      aria-label={label}
      className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${collapsed ? 'justify-center px-2' : ''}`}
      style={{
        background: active ? C.surface : 'transparent',
        color: active ? C.ink : C.ink2,
        boxShadow: active ? C.shadowSm : 'none',
      }}
      onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = C.line2; }}
      onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
    >
      <span className="flex-shrink-0 opacity-80">{icon}</span>
      {!collapsed && <span className="truncate">{label}</span>}
    </button>
  );
  return collapsed
    ? <div className="fb-tip-wrap" data-tip={label}>{body}</div>
    : body;
}

/**
 * Collapsible rail group — a single parent row (e.g. "Logs") that expands to
 * reveal its child links. Auto-opens when one of its children is the active
 * route. In the collapsed (icon-only) rail it degrades to a single icon that
 * opens the first child.
 */
function AppRailGroup({
  icon, label, collapsed, items, activeNav,
}: {
  icon: React.ReactNode;
  label: string;
  collapsed: boolean;
  items: { label: string; to: string; nav: string; icon: React.ReactNode }[];
  activeNav: string | undefined;
}) {
  const hasActive = items.some((i) => i.nav === activeNav);
  const [open, setOpen] = useState(hasActive);
  useEffect(() => { if (hasActive) setOpen(true); }, [hasActive]);

  if (collapsed) {
    return <AppRailItem icon={icon} label={label} collapsed active={hasActive} to={items[0]?.to} />;
  }

  return (
    <div className="flex flex-col gap-0.5">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors"
        style={{ background: 'transparent', color: hasActive ? C.ink : C.ink2 }}
        onMouseEnter={(e) => { e.currentTarget.style.background = C.line2; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
      >
        <span className="flex-shrink-0 opacity-80">{icon}</span>
        <span className="truncate flex-1 text-left">{label}</span>
        <ChevronDown size={14} className="flex-shrink-0" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s', opacity: 0.55 }} />
      </button>
      {open && (
        <div className="flex flex-col gap-0.5 ml-3 pl-2" style={{ borderLeft: `1px solid ${C.line}` }}>
          {items.map((i) => (
            <AppRailItem key={i.to} icon={i.icon} label={i.label} collapsed={false} active={activeNav === i.nav} to={i.to} />
          ))}
        </div>
      )}
    </div>
  );
}

export function useRailCollapsed() {
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    try { return localStorage.getItem('fb-rail-collapsed') === '1'; } catch { return false; }
  });
  const toggle = () => setCollapsed((v) => {
    const nv = !v;
    try { localStorage.setItem('fb-rail-collapsed', nv ? '1' : '0'); } catch {}
    return nv;
  });
  return { collapsed, toggle };
}

/**
 * The sidebar itself. `activeNav` lets the host page highlight the
 * relevant top-level item (e.g. "students" when on /students).
 */
export function AppRail({
  collapsed,
  activeNav,
}: {
  collapsed: boolean;
  activeNav?:
    // Entities
    | 'overview' | 'students' | 'companies' | 'drives' | 'imports'
    // Tools
    | 'query' | 'shortlister' | 'tests' | 'live-onsite' | 'practice-plans' | 'reports' | 'calendar' | 'autopilot' | 'help'
    // Library (umbrella + sub-views once they exist)
    | 'library' | 'questions' | 'resources' | 'skills'
    // Admin — AI ops review surfaces, only rendered for admin users
    | 'usage' | 'team' | 'settings'
    // Platform — super-admin only
    | 'chat-log' | 'platform-dashboard' | 'platform-waitlist' | 'platform-colleges' | 'platform-partners' | 'platform-audit' | 'platform-test-logs' | 'platform-media' | 'platform-watch100' | 'platform-blog' | 'platform-ecb-logs' | 'platform-system-health' | 'platform-speech-grading' | 'platform-support' | 'platform-paper-reviews' | 'platform-resume-builder'
    // Legacy alias — old `chat` activeNav still resolves to Query
    | 'chat';
}) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { profile } = useTeamProfile();
  const { logoUrl: brandLogo, name: brandName, logoScale } = useBranding();
  const handleLogout = () => { logout(); navigate('/'); };

  const userName = user?.name || user?.email || 'User';
  const isPlatformAdmin = user?.role === 'admin' && (user as any)?.level === 'system';
  // White-label partner login (admin.org / moderator.manage carrying a
  // partnerId) — gets a Partner console rail, not the TPO surfaces.
  const isPartner = !isPlatformAdmin && !!(user as any)?.partnerId;
  // Support desk agent (moderator.approve) — platform-tier but confined to
  // the ticket queue by PlatformLayoutRoute, so it gets its own one-item rail
  // rather than falling through to the tenant nav below.
  const isSupportAgent = user?.role === 'moderator' && (user as any)?.level === 'approve';
  // Reviewers read and annotate. They do not run shortlists, author tests or
  // ingest spreadsheets — which is what the chat surface exists to drive — so
  // its entry point is hidden and they land on Overview instead. Their rail is
  // the four screens they actually use, with no section headers: two labels
  // over four items is furniture, not navigation.
  const isReviewer = user?.role === 'moderator' && (user as any)?.level === 'review';
  // A department head (e.g. CSE HOD) is a branch-scoped member — they focus on
  // their branch's students, so placement-cell surfaces like Companies are
  // hidden from their rail.
  const isDeptHead = (profile.scopeBranches?.length ?? 0) > 0;
  // On a white-label instance, a regular TPO gets the slim Institution-admin
  // rail (Students / Tests / Reports, no chat).
  const slimTpo = !isPlatformAdmin && !isPartner && !isSupportAgent && IS_WHITELABEL;
  // A member's own job role beats the generic tier: a HOD reads "HoD — CSE"
  // and knows it is their account, where "Reviewer" is the same word every
  // reviewer in the college sees. Falls back to the tier when unset.
  const jobRole = (profile.department ?? '').trim();
  const userRole = isPlatformAdmin
    ? 'Platform Admin'
    : isSupportAgent ? 'Support'
    : isPartner ? (user?.role === 'admin' ? 'Partner Admin' : 'Partner Moderator')
    : slimTpo ? 'Institution admin'
    : jobRole ? jobRole
    // moderator.manage / .review are the Assistant TPO and Reviewer presets —
    // without these the fallback prints the raw role string, so an Assistant TPO
    // saw a lowercase "moderator" where the TPO sees a proper title. Mirrors
    // tierLabel() in platform.shared.ts, which the server already uses.
    : user?.role === 'moderator'
      ? ((user as any)?.level === 'review' ? 'Reviewer' : 'Assistant TPO')
    : (user?.role === 'admin' ? 'Placement Officer · Admin' : (user?.role ?? ''));

  return (<>
    <aside
      className={`relative flex-col hidden md:flex ${collapsed ? 'p-2' : 'p-3'}`}
      style={{
        width: collapsed ? 60 : 252,
        background: C.bg2,
        borderRight: `1px solid ${C.line}`,
        transition: 'width 220ms cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      {/* ── Sticky TOP — brand + New thread + search → /recent. Always visible. */}
      <div className={`flex flex-col flex-shrink-0 gap-2`} style={{ paddingBottom: 12 }}>
        <div className={`flex items-center h-11 ${collapsed ? 'justify-center' : 'px-1'}`}>
          <Link to={isPlatformAdmin ? '/platform' : isPartner ? '/platform/colleges' : slimTpo ? '/overview' : '/dashboard'} className="no-underline flex items-center" aria-label="Home">
            {brandLogo ? (
              <img src={brandLogo} alt="Logo" style={{ height: collapsed ? 30 : Math.round(38 * logoScale), width: collapsed ? 30 : 'auto', maxWidth: collapsed ? 30 : 190, objectFit: 'contain' }} />
            ) : brandName ? (
              <span style={{ fontWeight: 800, letterSpacing: '-0.02em', color: C.ink, fontSize: collapsed ? 18 : 22 }}>{collapsed ? brandName.charAt(0).toUpperCase() : brandName}</span>
            ) : (
              // Collapsed shows the round robot icon, which is a self-contained
              // avatar that sits fine on either canvas — only the wordmark needs
              // the dark-mode text treatment.
              collapsed
                ? <img src="/fresherbot-icon.png" alt="FresherBot" style={{ height: 30, width: 30, objectFit: 'contain' }} />
                : <FresherbotLogo height={30} />
            )}
          </Link>
        </div>

        {!isPlatformAdmin && !isPartner && !slimTpo && !isSupportAgent && !isReviewer && (
          <div className={collapsed ? 'fb-tip-wrap' : ''} data-tip={collapsed ? 'New thread' : undefined}>
            <button
              onClick={() => navigate('/dashboard')}
              aria-label="New thread"
              className={`w-full flex items-center gap-2 rounded-lg px-3 py-1.5 text-[13px] font-medium transition-colors ${collapsed ? 'justify-center px-2' : ''}`}
              style={{ background: C.surface, color: C.ink, boxShadow: C.shadowSm }}
            >
              <Plus size={14} className="flex-shrink-0" />
              {!collapsed && <span>New thread</span>}
            </button>
          </div>
        )}
      </div>

      {/* ── MIDDLE — nav sections. No inner overflow wrapper: any
             overflow ancestor clips fb-tip-wrap tooltips that intentionally
             extend past the rail's right edge. With a fixed nav (3+6+3
             items + 3 headings), the rail fits a typical viewport without
             scrolling; if it ever doesn't, restructure rather than
             reintroduce the clipping wrapper. */}
      {/* Nav block — Data Bank + Tools. NEVER overflow-scroll: an
          overflow ancestor would clip the fb-tip-wrap tooltips that
          extend past the rail's right edge. With a fixed nav size, this
          stays put. Recent Chats lives in its own scrollable area below. */}
      {/* gap-3 separates the Tools and Data Bank groups under their headings.
          Reviewers have no headings, so that gap just looks like a hole between
          Reports and Students — even it out to the in-group spacing. */}
      <div className={`flex-shrink-0 flex flex-col ${isReviewer ? 'gap-0.5 pt-3' : 'gap-3 pt-3'}`}>

      {/* Platform Dashboard label — shown only to super admins so they
          immediately see they are in cross-tenant mode. */}
      {isPlatformAdmin && !collapsed && (
        <div
          className="rounded-md px-3 py-2 mt-1 mb-1 flex items-center gap-2"
          style={{
            background: 'oklch(0.22 0.04 280)',
            color: 'oklch(0.96 0.02 280)',
            fontFamily: MONO,
            letterSpacing: '0.04em',
          }}
        >
          <ShieldCheck size={12} />
          <span className="text-[11px] uppercase font-semibold">Platform Admin</span>
        </div>
      )}

      {/* Platform — super-admin only. Cross-tenant operations under
          /platform/*. Shown FIRST for platform admins so it's the
          primary entry-point for them. */}
      {isPlatformAdmin && (
        <div className="flex flex-col gap-0.5">
          {!collapsed && (
            <div className="px-3 text-[10.5px] uppercase tracking-wider mt-1 mb-2" style={{ color: C.ink3, fontFamily: MONO }}>
              Platform Tools
            </div>
          )}
          <AppRailItem icon={<ShieldCheck size={14} />} label="Dashboard" collapsed={collapsed} active={activeNav === 'platform-dashboard'} to="/platform" />
          <AppRailItem icon={<Building2 size={14} />}   label="Institutions"  collapsed={collapsed} active={activeNav === 'platform-colleges'}  to="/platform/colleges" />
          <AppRailItem icon={<Handshake size={14} />}   label="Partners"  collapsed={collapsed} active={activeNav === 'platform-partners'}  to="/platform/partners" />
          <AppRailItem icon={<Users size={14} />}       label="Waitlist"  collapsed={collapsed} active={activeNav === 'platform-waitlist'}  to="/platform/waitlist" />
          <AppRailItem icon={<FileText size={14} />}    label="Resume builder" collapsed={collapsed} active={activeNav === 'platform-resume-builder'} to="/platform/resume-builder" />

          <AppRailGroup
            icon={<ClipboardList size={14} />} label="Logs" collapsed={collapsed} activeNav={activeNav}
            items={[
              { label: 'Chat log',  to: '/chat-log',          nav: 'chat-log',          icon: <MessageSquare size={14} /> },
              { label: 'Audit log', to: '/platform/audit',    nav: 'platform-audit',    icon: <ClipboardList size={14} /> },
              { label: 'Test logs', to: '/platform/test-logs', nav: 'platform-test-logs', icon: <ClipboardCheck size={14} /> },
              { label: 'Watch-100', to: '/platform/watch100', nav: 'platform-watch100', icon: <ShieldCheck size={14} /> },
              { label: 'Media',     to: '/platform/media',    nav: 'platform-media',    icon: <HardDrive size={14} /> },
              { label: 'ECB logs',  to: '/platform/ecb-logs', nav: 'platform-ecb-logs', icon: <ScrollText size={14} /> },
              { label: 'Speech grading', to: '/platform/speech-grading', nav: 'platform-speech-grading', icon: <Mic size={14} /> },
              { label: 'System health', to: '/platform/system-health', nav: 'platform-system-health', icon: <HardDrive size={14} /> },
            ]}
          />
          {/* Support sits OUTSIDE the Logs group on purpose: logs are things
              you read after the fact, this is a queue with people waiting.
              Paper reviews is the same shape — a support agent is blocked until
              an admin acts, and a pending paper is invisible in every library
              listing, so this is the only route to it. */}
          <AppRailItem icon={<LifeBuoy size={14} />} label="Support" collapsed={collapsed} active={activeNav === 'platform-support'} to="/platform/support" />
          <AppRailItem icon={<ClipboardCheck size={14} />} label="Paper reviews" collapsed={collapsed} active={activeNav === 'platform-paper-reviews'} to="/platform/paper-reviews" />
        </div>
      )}

      {/* Support desk — a moderator.approve agent gets this ONE item. Every
          other /platform path bounces them back here (PlatformLayoutRoute),
          so a fuller rail would just be dead links. */}
      {isSupportAgent && (
        <div className="flex flex-col gap-0.5">
          {!collapsed && (
            <div className="px-3 text-[10.5px] uppercase tracking-wider mt-1 mb-2" style={{ color: C.ink3, fontFamily: MONO }}>
              Support
            </div>
          )}
          <AppRailItem icon={<LifeBuoy size={14} />} label="Support" collapsed={collapsed} active={activeNav === 'platform-support'} to="/platform/support" />
          {/* Papers a support agent authors are held for a platform admin to
              approve before they reach the master library (reviewState on
              Test), so this is not the TPO test surface the rail otherwise
              keeps them out of. */}
          <AppRailItem icon={<ClipboardList size={14} />} label="Papers" collapsed={collapsed} active={activeNav === 'tests'} to="/tests" />
          {/* Support agents review each other: the queue excludes their own
              papers, and Approved shows only their own decisions. */}
          <AppRailItem icon={<ClipboardCheck size={14} />} label="Reviews" collapsed={collapsed} active={activeNav === 'platform-paper-reviews'} to="/platform/paper-reviews" />
        </div>
      )}

      {/* Partner Admin — white-label reseller console. Same layout as the
          platform rail; partner-scoped nav. */}
      {isPartner && !collapsed && (
        <div className="rounded-md px-3 py-2 mt-1 mb-1 flex items-center gap-2" style={{ background: 'oklch(0.30 0.07 165)', color: 'oklch(0.96 0.02 165)', fontFamily: MONO, letterSpacing: '0.04em' }}>
          <Handshake size={12} />
          <span className="text-[11px] uppercase font-semibold">Partner Admin</span>
        </div>
      )}
      {isPartner && (
        <div className="flex flex-col gap-0.5">
          {!collapsed && (
            <div className="px-3 text-[10.5px] uppercase tracking-wider mt-1 mb-2" style={{ color: C.ink3, fontFamily: MONO }}>
              Partner
            </div>
          )}
          {/* Partners reuse the platform Colleges page — the API scopes it to
              their own colleges. */}
          <AppRailItem icon={<Building2 size={14} />} label="Institutions" collapsed={collapsed} active={activeNav === 'platform-colleges'} to="/platform/colleges" />
          {/* Skills + Tests reuse the same library pages, scoped to master +
              this partner's own content (Master/Partner filter on the page). */}
          <AppRailItem icon={<Zap size={14} />}           label="Skills" collapsed={collapsed} active={activeNav === 'library' || activeNav === 'questions' || activeNav === 'resources' || activeNav === 'skills'} to="/skills" />
          <AppRailItem icon={<ClipboardList size={14} />} label="Tests"  collapsed={collapsed} active={activeNav === 'tests'} to="/tests" />
        </div>
      )}

      {/* White-label TPO — slim Institution-admin rail: Students / Tests / Reports. */}
      {slimTpo && !collapsed && (
        <div
          className="rounded-md px-3 py-2 mt-1 mb-1 flex items-center gap-2"
          style={{
            background: 'linear-gradient(90deg, color-mix(in oklch, var(--brand-accent, oklch(0.56 0.19 264)) 90%, black) 0%, var(--brand-accent, oklch(0.56 0.19 264)) 100%)',
            color: '#fff',
            border: '1px solid color-mix(in oklch, var(--brand-accent, oklch(0.56 0.19 264)) 70%, white)',
            boxShadow: '0 1px 3px color-mix(in oklch, var(--brand-accent, oklch(0.56 0.19 264)) 35%, transparent)',
            fontFamily: MONO,
            letterSpacing: '0.04em',
          }}
        >
          <Building2 size={12} />
          <span className="text-[11px] uppercase font-semibold">Institution Admin</span>
        </div>
      )}
      {slimTpo && (
        <div className="flex flex-col gap-0.5">
          <AppRailItem icon={<LayoutDashboard size={14} />} label="Overview" collapsed={collapsed} active={activeNav === 'overview'}  to="/overview" />
          <AppRailItem icon={<Users size={14} />}         label="Students" collapsed={collapsed} active={activeNav === 'students'} to="/students" />
          <AppRailItem icon={<ClipboardList size={14} />} label="Tests"    collapsed={collapsed} active={activeNav === 'tests'}    to="/tests" />
          <AppRailItem icon={<BarChart3 size={14} />}     label="Reports"  collapsed={collapsed} active={activeNav === 'reports'}  to="/reports" />
        </div>
      )}

      {/* Tools — minimal four. Hidden for platform admins + partners + slim TPO.
          Ordered above Data Bank — these are the day-to-day working screens;
          Data Bank is the curated stores they draw from, reached less often. */}
      {!isPlatformAdmin && !isPartner && !slimTpo && !isSupportAgent && (
      <div className="flex flex-col gap-0.5">
        {!collapsed && !isReviewer && (
          <div className="px-3 text-[10.5px] uppercase tracking-wider mt-1 mb-2" style={{ color: C.ink3, fontFamily: MONO }}>
            Tools
          </div>
        )}
        {/* Query removed — "New thread" at top of rail is the same entry point. */}
        {/* Overview — onboarding + engagement (active/practicing) + branch
            breakdown at a glance. Was already a route/page (white-label TPOs
            land here instead of the chat) but had no nav entry point for a
            regular TPO.

            It used to be hidden from department heads, because the branch
            breakdown spanned the whole institution. /api/overview now applies
            the caller's branch, institution and year scope, so a HoD sees their
            own branch and nothing else — verified as the ECE HoD: one branch,
            24 students, and an explicit ?branch=CSE ignored rather than
            honoured. Shortlister and Drives below stay hidden; those are
            placement-cell tools, which is a different question from scope. */}
        <AppRailItem icon={<Gauge size={14} />}          label="Overview"       collapsed={collapsed} active={activeNav === 'overview'} to="/overview" />
        {/* Shortlister — rule-based filter that ranks students against
            company / drive criteria (CGPA, branch, skills, prefs). Sits
            above Tests because it's the first thing a TPO does for a
            campus drive. */}
        {/* Shortlister + Campus Drives are placement-cell tools — hidden for
            department heads, who focus on their branch's students. */}
        {!isDeptHead && (
          <AppRailItem icon={<Filter size={14} />}        label="Shortlister"    collapsed={collapsed} active={activeNav === 'shortlister'} to="/shortlist" />
        )}
        {/* Tests — composed assessments built from the Skills library */}
        <AppRailItem icon={<ClipboardList size={14} />} label="Tests"          collapsed={collapsed} active={activeNav === 'tests'} to="/tests" />
        {/* Live onsite — who signed in recently and which sittings are open.
            Sits under Tests: it is what you watch while one is running. */}
        <AppRailItem icon={<Radio size={14} />}         label="Live onsite"    collapsed={collapsed} active={activeNav === 'live-onsite'} to="/live-onsite" />
        {/* Practice Plans — beta; moved into Settings → Practice Plans while it
            bakes, so it's not surfaced to every TPO in the main rail yet. */}
        <AppRailItem icon={<BarChart3 size={14} />}     label="Reports"        collapsed={collapsed} active={activeNav === 'reports'} to="/reports" />
        {/* Help — moved into Settings → Help. */}
        {DRIVES_ENABLED && !isDeptHead && (
          <AppRailItem icon={<Briefcase size={14} />}     label="Campus Drives"   collapsed={collapsed} active={activeNav === 'drives'} to="/drives" />
        )}
      </div>
      )}

      {/* Data Bank — the curated stores tools draw from. For platform
          admins we hide Students (per-college data) but keep Companies +
          Skills Library which are global / catalog-level. Hidden for partners. */}
      {!isPartner && !slimTpo && !isSupportAgent && (
      <div className="flex flex-col gap-0.5">
        {!collapsed && !isReviewer && (
          <div className="px-3 text-[10.5px] uppercase tracking-wider mt-1 mb-2" style={{ color: C.ink3, fontFamily: MONO }}>
            Data Bank
          </div>
        )}
        {!isPlatformAdmin && (
          <AppRailItem icon={<Users size={14} />}     label="Students"  collapsed={collapsed} active={activeNav === 'students'}  to="/students" />
        )}
        {!isDeptHead && (
          <AppRailItem
            icon={<Building2 size={14} />}
            label={isPlatformAdmin ? 'Master Companies' : 'Companies'}
            collapsed={collapsed}
            active={activeNav === 'companies'}
            to="/companies"
          />
        )}
        {/* Skills — skill-first umbrella. Questions and Resources live as
            tabs inside the skill detail; bulk import auto-tags by skill. */}
        {!isReviewer && (
          <AppRailItem
            icon={<Zap size={14} />}
            label={isPlatformAdmin ? 'Master Skills' : 'Skills Library'}
            collapsed={collapsed}
            active={activeNav === 'library' || activeNav === 'questions' || activeNav === 'resources' || activeNav === 'skills'}
            to="/skills"
          />
        )}
        {/* Master Tests is the global library of reusable assessments
            available to every college. The same /tests route is used by
            tenant admins (their tenant-scoped tests), so we just relabel
            the entry for platform admin. */}
        {isPlatformAdmin && (
          <AppRailItem
            icon={<ClipboardList size={14} />}
            label="Master Tests"
            collapsed={collapsed}
            active={activeNav === 'tests'}
            to="/tests"
          />
        )}
        {/* Master Plans — global library of practice plans every
            college can clone. Same /plans route is reused; only the
            label changes for platform admin. */}
        {isPlatformAdmin && (
          <AppRailItem
            icon={<Sparkles size={14} />}
            label="Master Plans"
            collapsed={collapsed}
            active={activeNav === 'practice-plans'}
            to="/plans"
          />
        )}
      </div>
      )}

      {/* Settings — admin tooling lives under /settings/* (Team, Users,
          Plan, Usage, Audit). Cross-tenant Chat log + Waitlist live in
          /platform/* and aren't shown here (super-admin only). */}

      </div>{/* /nav block */}

      {/* Recent chats — only when expanded; collapsed has no room for
          titles. Wrapped in its OWN overflow container so growing thread
          history doesn't push the bottom user/logout block off-screen.
          fb-noscrollbar hides the bar; thread rows have no tooltips so
          there's nothing to clip. */}
      {!collapsed && !isPlatformAdmin && !isPartner && !slimTpo && !isReviewer && (
        <div className="flex-1 min-h-0 overflow-y-auto fb-noscrollbar mt-3 pt-3" style={{ borderTop: `1px solid ${C.line}` }}>
          <RailRecentChats />
        </div>
      )}
      {/* Pushes the bottom user / logout block down for anyone without the
          Recent-chats panel above — that panel is `flex-1` and is what normally
          fills the gap. Reviewers belong here too: hiding their chat left the
          profile card floating directly under the last nav item. */}
      {(isPlatformAdmin || isPartner || slimTpo || isReviewer || collapsed) && <div className="flex-1" />}

      {/* ── Sticky BOTTOM — Settings cog + user block + sign-out. */}
      {collapsed ? (
        <div className="fb-rail-bottom flex flex-col items-center gap-2 pt-3 flex-shrink-0" style={{ borderTop: `1px solid ${C.line}` }}>
          {!isSupportAgent && <Link
            to="/settings"
            title="Settings"
            data-tip="Settings"
            className="fb-tip-wrap rounded-md p-1.5 transition-colors"
            style={{ color: activeNav === 'settings' ? C.ink : C.ink3 }}
            onMouseEnter={(e) => { e.currentTarget.style.background = C.line2; e.currentTarget.style.color = C.ink; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = activeNav === 'settings' ? C.ink : C.ink3; }}
          >
            <SettingsIcon size={14} />
          </Link>}
          <button
            onClick={handleLogout}
            title="Sign out"
            className="fb-tip-wrap rounded-md p-1.5 transition-colors"
            data-tip="Sign out"
            style={{ color: C.ink3 }}
            onMouseEnter={(e) => { e.currentTarget.style.background = C.line2; e.currentTarget.style.color = C.ink; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = C.ink3; }}
          >
            <LogOut size={14} />
          </button>
          <div className="fb-tip-wrap" data-tip={userName}>
            <div
              className="size-8 rounded-full flex items-center justify-center text-xs font-semibold"
              style={{ background: C.accent, color: 'white' }}
            >
              {(userName[0] || 'U').toUpperCase()}
            </div>
          </div>
        </div>
      ) : (
        <Link
          to={isSupportAgent ? '/platform/support' : '/settings'}
          className="fb-rail-bottom group flex items-center gap-2.5 pt-3 px-1 flex-shrink-0 hover:opacity-90"
          style={{ borderTop: `1px solid ${C.line}`, textDecoration: 'none' }}
          title={isSupportAgent ? 'Support' : 'Open settings'}
        >
          <div
            className="size-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0"
            style={{ background: C.accent, color: 'white' }}
          >
            {(userName[0] || 'U').toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[12.5px] font-medium truncate" style={{ color: C.ink }}>{userName}</div>
            <div className="text-[11px] truncate" style={{ color: C.ink3 }}>{userRole}</div>
          </div>
          {!isSupportAgent && (
            <span
              className="flex-shrink-0 rounded-md p-1.5 transition-colors"
              style={{ color: activeNav === 'settings' ? C.ink : C.ink3 }}
            >
              <SettingsIcon size={14} />
            </span>
          )}
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleLogout(); }}
            title="Sign out"
            className="flex-shrink-0 rounded-md p-1.5 transition-colors"
            style={{ color: C.ink3 }}
            onMouseEnter={(e) => { e.currentTarget.style.background = C.line2; e.currentTarget.style.color = C.ink; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = C.ink3; }}
          >
            <LogOut size={14} />
          </button>
        </Link>
      )}
    </aside>

    <MobileRailDrawer activeNav={activeNav} />
  </>);
}

/**
 * Inline Recent + Pinned chats inside the expanded rail. Hydrates from
 * cache for instant paint, refreshes from API. Capped at 8 visible rows
 * (Pinned + Recent combined); a "View all" link goes to /recent for the
 * full list with search and pagination.
 */
function RailRecentChats() {
  const navigate = useNavigate();
  const [threads, setThreads] = useState<ThreadMeta[]>(() => listThreads());
  // Track which thread (if any) is currently streaming a reply, so we can
  // paint a soft "typing…" indicator in its row even if the user is on
  // another page. Driven by useChatEngine via a custom event.
  const [streamingId, setStreamingId] = useState<string | null>(streamingState.activeThreadId);

  useEffect(() => {
    refreshThreads().then(setThreads).catch(() => {/* keep cache */});
    const onStorage = () => setThreads(listThreads());
    const onStream = () => setStreamingId(streamingState.activeThreadId);
    window.addEventListener('storage', onStorage);
    window.addEventListener(STREAMING_EVENT, onStream);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(STREAMING_EVENT, onStream);
    };
  }, []);

  if (threads.length === 0) return null;

  const pinned = threads.filter((t) => t.pinned);
  const recentBudget = Math.max(0, 3 - pinned.length);
  const recent = threads.filter((t) => !t.pinned).slice(0, recentBudget);

  const Row = ({ t }: { t: ThreadMeta }) => {
    const isStreaming = streamingId === t.id;
    return (
      <button
        key={t.id}
        onClick={() => navigate(`/dashboard?thread=${t.id}`)}
        className="w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-left transition-colors"
        style={{ color: C.ink2 }}
        onMouseEnter={(e) => { e.currentTarget.style.background = C.line2; e.currentTarget.style.color = C.ink; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = C.ink2; }}
        title={t.title}
      >
        {t.pinned ? <Pin size={12} style={{ color: C.accent }} className="flex-shrink-0" /> : <MessageSquare size={12} style={{ color: C.ink4 }} className="flex-shrink-0" />}
        <span className="flex-1 min-w-0 truncate text-[13px]">{t.title}</span>
        {isStreaming && (
          <span className="flex-shrink-0 inline-flex items-center gap-0.5" title="FresherBot is replying…">
            <span className="fb-dot" style={{ animationDelay: '0ms' }} />
            <span className="fb-dot" style={{ animationDelay: '150ms' }} />
            <span className="fb-dot" style={{ animationDelay: '300ms' }} />
          </span>
        )}
        {t.shareToken && (
          <span className="flex-shrink-0 inline-flex" title={shareStatusLabel(t.shareExpiresAt)}>
            <Lock size={10} style={{ color: 'oklch(0.55 0.14 145)' }} />
          </span>
        )}
        <span className="text-[11px] flex-shrink-0" style={{ color: C.ink4 }}>{formatThreadTime(t.updatedAt)}</span>
      </button>
    );
  };

  return (
    <div className="flex flex-col gap-2 mt-2">
      {pinned.length > 0 && (
        <div className="flex flex-col">
          <div className="px-3 text-[11px] uppercase tracking-wider mt-1 mb-2" style={{ color: C.ink3, fontFamily: MONO }}>
            Pinned
          </div>
          {pinned.map((t) => <Row key={t.id} t={t} />)}
        </div>
      )}
      {recent.length > 0 && (
        <div className="flex flex-col">
          <div className="flex items-center justify-between px-3 mb-1">
            <span className="text-[11px] uppercase tracking-wider" style={{ color: C.ink3, fontFamily: MONO }}>
              Recent
            </span>
            <Link to="/recent" className="text-[11px] hover:underline" style={{ color: C.accent }}>
              All →
            </Link>
          </div>
          {recent.map((t) => <Row key={t.id} t={t} />)}
        </div>
      )}
    </div>
  );
}

/**
 * Mobile-only drawer rendition of the rail. Activated by the AppTopBar
 * hamburger via the drawerStore. Renders the same nav items as the
 * desktop rail (forced expanded for legibility on mobile) inside an
 * overlay with a tap-to-close backdrop.
 *
 * Kept as a standalone component so it doesn't interfere with the
 * desktop rail's collapse/expand state — they're independent surfaces.
 */
function MobileRailDrawer({ activeNav }: { activeNav?: string }) {
  const open = useDrawerOpen();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { profile } = useTeamProfile();
  const { logoUrl: brandLogo, name: brandName, logoScale } = useBranding();
  // Collapsible "Logs" group — mirrors the desktop rail so mobile matches.
  const logsNavs = ['chat-log', 'platform-audit', 'platform-test-logs', 'platform-watch100', 'platform-media', 'platform-ecb-logs', 'platform-system-health'];
  const [logsOpen, setLogsOpen] = useState(logsNavs.includes(activeNav ?? ''));
  if (!open) return null;

  const userName = user?.name || user?.email || 'User';
  const isPlatformAdmin = user?.role === 'admin' && (user as any)?.level === 'system';
  const isPartner = !isPlatformAdmin && !!(user as any)?.partnerId;
  // Support desk agent (moderator.approve) — platform-tier but confined to
  // the ticket queue by PlatformLayoutRoute, so it gets its own one-item rail
  // rather than falling through to the tenant nav below.
  const isSupportAgent = user?.role === 'moderator' && (user as any)?.level === 'approve';
  const isDeptHead = (profile.scopeBranches?.length ?? 0) > 0;
  // On a white-label instance, a regular TPO gets the slim Institution-admin
  // rail (Students / Tests / Reports, no chat).
  const slimTpo = !isPlatformAdmin && !isPartner && !isSupportAgent && IS_WHITELABEL;
  // A member's own job role beats the generic tier: a HOD reads "HoD — CSE"
  // and knows it is their account, where "Reviewer" is the same word every
  // reviewer in the college sees. Falls back to the tier when unset.
  const jobRole = (profile.department ?? '').trim();
  const userRole = isPlatformAdmin
    ? 'Platform Admin'
    : isSupportAgent ? 'Support'
    : isPartner ? (user?.role === 'admin' ? 'Partner Admin' : 'Partner Moderator')
    : slimTpo ? 'Institution admin'
    : jobRole ? jobRole
    // moderator.manage / .review are the Assistant TPO and Reviewer presets —
    // without these the fallback prints the raw role string, so an Assistant TPO
    // saw a lowercase "moderator" where the TPO sees a proper title. Mirrors
    // tierLabel() in platform.shared.ts, which the server already uses.
    : user?.role === 'moderator'
      ? ((user as any)?.level === 'review' ? 'Reviewer' : 'Assistant TPO')
    : (user?.role === 'admin' ? 'Placement Officer · Admin' : (user?.role ?? ''));
  const close = () => closeMobileRail();
  const go = (path: string) => { navigate(path); close(); };
  const handleLogout = () => { logout(); navigate('/'); close(); };

  const Section = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="flex flex-col gap-0.5">
      <div className="px-3 text-[10.5px] uppercase tracking-wider mt-1 mb-2" style={{ color: C.ink3, fontFamily: MONO }}>{label}</div>
      {children}
    </div>
  );
  const Item = ({ icon, label, to, active }: { icon: React.ReactNode; label: string; to?: string; active?: boolean }) => (
    <button
      onClick={() => to ? go(to) : undefined}
      className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors"
      style={{
        background: active ? C.surface : 'transparent',
        color: active ? C.ink : C.ink2,
        boxShadow: active ? C.shadowSm : 'none',
      }}
    >
      <span className="flex-shrink-0 opacity-80">{icon}</span>
      <span className="truncate">{label}</span>
    </button>
  );

  return (
    <div className="md:hidden fixed inset-0 z-50 flex">
      <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.4)' }} onClick={close} />
      <aside
        className="relative flex flex-col p-3 overflow-y-auto"
        style={{ width: 280, background: C.bg2, borderRight: `1px solid ${C.line}` }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Sticky-ish top */}
        <div className="flex items-center justify-between mb-2">
          <Link to={isPlatformAdmin ? '/platform' : isPartner ? '/platform/colleges' : slimTpo ? '/overview' : '/dashboard'} onClick={close} className="flex items-center" aria-label="Home">
            {brandLogo ? (
              <img src={brandLogo} alt="Logo" style={{ height: Math.round(34 * logoScale), width: 'auto', maxWidth: 200, objectFit: 'contain' }} />
            ) : brandName ? (
              <span style={{ fontWeight: 800, letterSpacing: '-0.02em', color: C.ink, fontSize: 20 }}>{brandName}</span>
            ) : (
              <FresherbotLogo height={28} />
            )}
          </Link>
          <button onClick={close} className="size-7 flex items-center justify-center rounded-md" style={{ color: C.ink3 }} aria-label="Close menu">
            <X size={16} />
          </button>
        </div>

        {!isPlatformAdmin && !isPartner && !slimTpo && !isSupportAgent && (
          <button
            onClick={() => go('/dashboard')}
            className="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium mb-2"
            style={{ background: C.surface, color: C.ink, boxShadow: C.shadowSm }}
          >
            <Plus size={14} /> New thread
          </button>
        )}

        <div className="flex flex-col gap-2 flex-1">
          {isPlatformAdmin && (
            <>
            <Section label="Platform Tools">
              <Item icon={<ShieldCheck size={14} />} label="Dashboard" to="/platform" active={activeNav === 'platform-dashboard'} />
              <Item icon={<Building2 size={14} />} label="Institutions" to="/platform/colleges" active={activeNav === 'platform-colleges'} />
              <Item icon={<Handshake size={14} />} label="Partners" to="/platform/partners" active={activeNav === 'platform-partners'} />
              <Item icon={<Users size={14} />} label="Waitlist" to="/platform/waitlist" active={activeNav === 'platform-waitlist'} />
              <Item icon={<FileText size={14} />} label="Resume builder" to="/platform/resume-builder" active={activeNav === 'platform-resume-builder'} />
            </Section>
            <div className="flex flex-col gap-0.5">
              <button onClick={() => setLogsOpen((o) => !o)}
                className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm"
                style={{ background: 'transparent', color: logsNavs.includes(activeNav ?? '') ? C.ink : C.ink2 }}>
                <span className="flex-shrink-0 opacity-80"><ClipboardList size={14} /></span>
                <span className="truncate flex-1 text-left">Logs</span>
                <ChevronDown size={14} className="flex-shrink-0" style={{ transform: logsOpen ? 'rotate(180deg)' : 'none', transition: 'transform .15s', opacity: 0.55 }} />
              </button>
              {logsOpen && (
                <div className="flex flex-col gap-0.5 ml-3 pl-2" style={{ borderLeft: `1px solid ${C.line}` }}>
                  <Item icon={<MessageSquare size={14} />} label="Chat log" to="/chat-log" active={activeNav === 'chat-log'} />
                  <Item icon={<ClipboardList size={14} />} label="Audit log" to="/platform/audit" active={activeNav === 'platform-audit'} />
                  <Item icon={<ClipboardCheck size={14} />} label="Test logs" to="/platform/test-logs" active={activeNav === 'platform-test-logs'} />
                  <Item icon={<ShieldCheck size={14} />} label="Watch-100" to="/platform/watch100" active={activeNav === 'platform-watch100'} />
                  <Item icon={<HardDrive size={14} />} label="Media" to="/platform/media" active={activeNav === 'platform-media'} />
                  <Item icon={<ScrollText size={14} />} label="ECB logs" to="/platform/ecb-logs" active={activeNav === 'platform-ecb-logs'} />
                  <Item icon={<Mic size={14} />} label="Speech grading" to="/platform/speech-grading" active={activeNav === 'platform-speech-grading'} />
                  <Item icon={<HardDrive size={14} />} label="System health" to="/platform/system-health" active={activeNav === 'platform-system-health'} />
                </div>
              )}
              <Item icon={<LifeBuoy size={14} />} label="Support" to="/platform/support" active={activeNav === 'platform-support'} />
            </div>
            </>
          )}
          {isSupportAgent && (
            <Section label="Support">
              <Item icon={<LifeBuoy size={14} />} label="Support" to="/platform/support" active={activeNav === 'platform-support'} />
              <Item icon={<ClipboardList size={14} />} label="Papers" to="/tests" active={activeNav === 'tests'} />
              <Item icon={<ClipboardCheck size={14} />} label="Reviews" to="/platform/paper-reviews" active={activeNav === 'platform-paper-reviews'} />
            </Section>
          )}
          {isPartner && (
            <Section label="Partner">
              <Item icon={<Building2 size={14} />} label="Institutions" to="/platform/colleges" active={activeNav === 'platform-colleges'} />
              <Item icon={<Zap size={14} />} label="Skills" to="/skills" active={activeNav === 'library' || activeNav === 'skills' || activeNav === 'questions' || activeNav === 'resources'} />
              <Item icon={<ClipboardList size={14} />} label="Tests" to="/tests" active={activeNav === 'tests'} />
            </Section>
          )}
          {slimTpo && (
            <Section label="Institution Admin">
              <Item icon={<LayoutDashboard size={14} />} label="Overview" to="/overview" active={activeNav === 'overview'} />
              <Item icon={<Users size={14} />} label="Students" to="/students" active={activeNav === 'students'} />
              <Item icon={<ClipboardList size={14} />} label="Tests" to="/tests" active={activeNav === 'tests'} />
              <Item icon={<BarChart3 size={14} />} label="Reports" to="/reports" active={activeNav === 'reports'} />
            </Section>
          )}
          {!isPlatformAdmin && !isPartner && !slimTpo && !isSupportAgent && (
            <Section label="Tools">
              <Item icon={<Gauge size={14} />} label="Overview" to="/overview" active={activeNav === 'overview'} />
              {!isDeptHead && (
                <Item icon={<Filter size={14} />} label="Shortlister" to="/shortlist" active={activeNav === 'shortlister'} />
              )}
              <Item icon={<ClipboardList size={14} />} label="Tests" to="/tests" active={activeNav === 'tests'} />
              {/* Practice Plans — beta; lives under Settings → Practice Plans for now. */}
              {DRIVES_ENABLED && !isDeptHead && (
                <Item icon={<Briefcase size={14} />} label="Campus Drives" to="/drives" active={activeNav === 'drives'} />
              )}
            </Section>
          )}
          {!isPartner && !slimTpo && !isSupportAgent && (
          <Section label="Data Bank">
            {!isPlatformAdmin && (
              <Item icon={<Users size={14} />} label="Students" to="/students" active={activeNav === 'students'} />
            )}
            {!isDeptHead && (
              <Item icon={<Building2 size={14} />} label={isPlatformAdmin ? 'Master Companies' : 'Companies'} to="/companies" active={activeNav === 'companies'} />
            )}
            <Item icon={<Zap size={14} />} label={isPlatformAdmin ? 'Master Skills' : 'Skills Library'} to="/skills" active={activeNav === 'library' || activeNav === 'questions' || activeNav === 'resources' || activeNav === 'skills'} />
            {isPlatformAdmin && (
              <Item icon={<ClipboardList size={14} />} label="Master Tests" to="/tests" active={activeNav === 'tests'} />
            )}
            {isPlatformAdmin && (
              <Item icon={<Sparkles size={14} />} label="Master Plans" to="/plans" active={activeNav === 'practice-plans'} />
            )}
          </Section>
          )}
        </div>

        <div className="flex items-center gap-2.5 pt-3 px-1" style={{ borderTop: `1px solid ${C.line}` }}>
          <div className="size-8 rounded-full flex items-center justify-center text-xs font-semibold" style={{ background: C.accent, color: 'white' }}>
            {(userName[0] || 'U').toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[12.5px] font-medium truncate" style={{ color: C.ink }}>{userName}</div>
            <div className="text-[11px] truncate" style={{ color: C.ink3 }}>{userRole}</div>
          </div>
          {!isSupportAgent && (
            <button onClick={() => go('/settings')} title="Settings" className="rounded-md p-1.5" style={{ color: activeNav === 'settings' ? C.ink : C.ink3 }}>
              <SettingsIcon size={14} />
            </button>
          )}
          <button onClick={handleLogout} title="Sign out" className="rounded-md p-1.5" style={{ color: C.ink3 }}>
            <LogOut size={14} />
          </button>
        </div>
      </aside>
    </div>
  );
}

/** Thin top bar with a sidebar-collapse toggle + optional title. */
export function AppTopBar({
  collapsed,
  onToggle,
  title,
  right,
  hideScope,
}: {
  collapsed: boolean;
  onToggle: () => void;
  title?: React.ReactNode;
  right?: React.ReactNode;
  /** Drop the institution + year scope chips. Used by platform-level
   *  surfaces (super-admin) where the chips don't apply because the
   *  view is cross-tenant. */
  hideScope?: boolean;
}) {
  // Auto-hide for super-admins regardless of which shell rendered
  // this bar — they sometimes navigate into TPO-style pages
  // (/companies, /students) and we don't want the year/institution
  // chips or the per-tenant bell showing up there either.
  const { user } = useAuth();
  const isPlatformAdmin = (user as any)?.role === 'admin' && (user as any)?.level === 'system';
  const isSupportAgent = (user as any)?.role === 'moderator' && (user as any)?.level === 'approve';
  hideScope = hideScope || isPlatformAdmin || isSupportAgent;
  const { isDark, toggle: toggleTheme } = useDarkMode();
  const themeLabel = isDark ? 'Switch to light mode' : 'Switch to dark mode';
  return (
    <div
      className="flex items-center gap-2.5 px-3 sm:px-5 py-2.5 flex-shrink-0"
      style={{ borderBottom: `1px solid ${C.line}`, background: C.topbar, backdropFilter: 'blur(8px)' }}
    >
      {/* Mobile-only hamburger — opens the drawer rendition of the rail. */}
      <button
        onClick={openMobileRail}
        title="Open menu"
        className="md:hidden flex items-center justify-center size-7 rounded-md transition-colors"
        style={{ color: C.ink3 }}
        aria-label="Open menu"
      >
        <Menu size={16} />
      </button>

      {/* Desktop-only collapse toggle */}
      <button
        onClick={onToggle}
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        className="hidden md:flex items-center justify-center size-6 rounded-md transition-colors"
        style={{ color: C.ink3 }}
        onMouseEnter={(e) => { e.currentTarget.style.background = C.line2; e.currentTarget.style.color = C.ink; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = C.ink3; }}
      >
        <Sidebar size={15} />
      </button>
      <div className="text-[12.5px] font-medium flex-1 min-w-0 truncate" style={{ color: C.ink }}>
        {title}
      </div>
      {/* Light / dark toggle. Lives here rather than in the rail footer so
          it's reachable from every shell (including the ones that render a
          slim rail) and doesn't move when the sidebar collapses. */}
      <button
        onClick={toggleTheme}
        title={themeLabel}
        aria-label={themeLabel}
        className="flex items-center justify-center size-7 rounded-md transition-colors flex-shrink-0"
        style={{ color: C.ink3 }}
        onMouseEnter={(e) => { e.currentTarget.style.background = C.line2; e.currentTarget.style.color = C.ink; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = C.ink3; }}
      >
        {isDark ? <Sun size={15} /> : <Moon size={15} />}
      </button>
      {!hideScope && <InstitutionChip />}
      {!hideScope && <DivisionChip />}
      {!hideScope && <YearChip />}
      {right}
    </div>
  );
}

// ---- Active-scope chips (year + institution). Surfaced next to the
// college chip so a TPO can pin the current placing batch / campus
// once and have every chat query / report inherit it as implicit
// context. The `'all'` value means "no constraint" — chat doesn't
// add a filter for that dimension.
const ACTIVE_YEAR_KEY = 'fb.activeYear';
const ACTIVE_INSTITUTION_KEY = 'fb.activeInstitution';
const YEAR_OPTIONS = [2024, 2025, 2026, 2027, 2028, 2029, 2030];

/**
 * The college these chips belong to.
 *
 * The scope chips used to be stored under one global key each, so a campus
 * picked while signed into one college stayed selected when a different
 * college signed in on the same browser. Their students list then filtered on
 * a campus that is not theirs — an empty or wrong roster, with another
 * college's campus name visible in the rail.
 *
 * Namespacing by tenant fixes both halves: nothing crosses between colleges,
 * and each college keeps its own scope rather than resetting whenever someone
 * shares a machine.
 */
function scopeTenant(): string {
  if (typeof window === 'undefined') return 'anon';
  try {
    const raw = window.localStorage.getItem('auth_user');
    if (!raw) return 'anon';
    const u = JSON.parse(raw) as { tenantId?: string | null; partnerId?: string | null };
    return u.tenantId || u.partnerId || 'anon';
  } catch { return 'anon'; }
}

/** `fb.activeInstitution` -> `fb.activeInstitution::<tenant>`. */
function scopedKey(base: string): string {
  return `${base}::${scopeTenant()}`;
}

/**
 * Drop the pre-namespace keys.
 *
 * They cannot be migrated: there is no record of which college the stored
 * value belonged to, and guessing it is the current one is exactly the bug.
 * Losing one deliberately-set chip once is the cost of not showing a college
 * someone else's campus.
 */
function dropLegacyScopeKeys(): void {
  if (typeof window === 'undefined') return;
  for (const k of ['fb.activeYear', 'fb.activeInstitution', 'fb.activeDivision']) {
    try { window.localStorage.removeItem(k); } catch { /* private mode */ }
  }
}

/** Read once, default to the actively-placing batch (next-up grad
 *  year given today's date). Computed in calendar terms — Indian
 *  placements run Jul–Mar against the *graduating* batch, so the
 *  active year is the current calendar year if before July, else
 *  next year. */
function defaultActiveYear(): number {
  // Jul-Dec 2026 places the batch graduating mid-2027, so the active year is
  // getFullYear() + 1; Jan-Jun places the batch graduating that same summer, so
  // it is getFullYear(). This previously returned one year MORE than that in
  // both branches (+1 / +2), which is one batch further out than anyone is
  // placing: in September 2026 it opened on 2028, a batch of 1,771 students,
  // while 7,412 — and every one of Anurag's 2,672 — graduate in 2027. Every TPO
  // and HoD landed on an empty roster and had to change the filter before
  // seeing anybody.
  const d = new Date();
  return d.getMonth() < 6 ? d.getFullYear() : d.getFullYear() + 1;
}

/** `'all'` = no year constraint; otherwise a 4-digit year. */
export function getActiveYear(): number | 'all' {
  if (typeof window === 'undefined') return defaultActiveYear();
  dropLegacyScopeKeys();
  const raw = window.localStorage.getItem(scopedKey(ACTIVE_YEAR_KEY));
  if (raw === 'all') return 'all';
  const n = raw ? Number(raw) : NaN;
  if (Number.isFinite(n) && YEAR_OPTIONS.includes(n)) return n;
  return defaultActiveYear();
}

export function setActiveYear(y: number | 'all') {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(scopedKey(ACTIVE_YEAR_KEY), String(y));
  window.dispatchEvent(new Event('fb:active-year-changed'));
}

export function useActiveYear(): [number | 'all', (y: number | 'all') => void] {
  const [y, setY] = useState<number | 'all'>(() => getActiveYear());
  useEffect(() => {
    const handler = () => setY(getActiveYear());
    window.addEventListener('fb:active-year-changed', handler);
    return () => window.removeEventListener('fb:active-year-changed', handler);
  }, []);
  return [y, (next: number | 'all') => { setActiveYear(next); setY(next); }];
}

/** `'all'` = every campus in the tenant; otherwise the institution name. */
export function getActiveInstitution(): string | 'all' {
  if (typeof window === 'undefined') return 'all';
  const raw = window.localStorage.getItem(scopedKey(ACTIVE_INSTITUTION_KEY));
  if (!raw || raw === 'all') return 'all';
  return raw;
}

export function setActiveInstitution(inst: string | 'all') {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(scopedKey(ACTIVE_INSTITUTION_KEY), inst);
  window.dispatchEvent(new Event('fb:active-institution-changed'));
}

export function useActiveInstitution(): [string | 'all', (i: string | 'all') => void] {
  const [i, setI] = useState<string | 'all'>(() => getActiveInstitution());
  useEffect(() => {
    const handler = () => setI(getActiveInstitution());
    window.addEventListener('fb:active-institution-changed', handler);
    return () => window.removeEventListener('fb:active-institution-changed', handler);
  }, []);
  return [i, (next: string | 'all') => { setActiveInstitution(next); setI(next); }];
}

/**
 * What this institution calls a division of its student body.
 *
 * "Branch" at an engineering college, "Class" at a school, "Track" at a
 * training vendor. The value is resolved server-side (college override ->
 * DIVISION_LABEL -> "Branch") so every surface agrees on one answer.
 *
 * The underlying column is Student.branch in all three cases — the three are
 * the same shape, a named division a staff member can be scoped to, and only
 * the word differs.
 */
export function useDivisionLabel(): string {
  const college = useCollege();
  return college?.divisionLabel || 'Branch';
}

/**
 * Plural of a division label, for "All branches" / "All classes" / "All tracks".
 *
 * Only the three sibilant endings need -es; everything else takes -s. This is
 * deliberately not a general English pluraliser — it handles the labels a
 * deployment actually sets, and a label it cannot pluralise correctly is a
 * sign the label is wrong rather than that this needs more rules.
 */
function pluraliseDivision(label: string): string {
  return /(?:ch|sh|ss|s|x|z)$/i.test(label) ? `${label}es` : `${label}s`;
}

// ---- Active-division scope. Mirrors the institution + year chips: a broad,
// orthogonal axis every page inherits. A member restricted to certain
// divisions never sees the others as options, because /api/student/branches
// is team-scoped — the picker offers exactly what they may ask for.
const ACTIVE_DIVISION_KEY = 'fb.activeDivision';

/** `'all'` = every division in scope; otherwise one division's name. */
export function getActiveDivision(): string {
  if (typeof window === 'undefined') return 'all';
  return window.localStorage.getItem(scopedKey(ACTIVE_DIVISION_KEY)) || 'all';
}
export function setActiveDivision(v: string) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(scopedKey(ACTIVE_DIVISION_KEY), v);
  window.dispatchEvent(new Event('fb:active-division-changed'));
}
export function useActiveDivision(): [string, (v: string) => void] {
  const [val, setVal] = useState<string>(() => getActiveDivision());
  useEffect(() => {
    const handler = () => setVal(getActiveDivision());
    window.addEventListener('fb:active-division-changed', handler);
    return () => window.removeEventListener('fb:active-division-changed', handler);
  }, []);
  // Stable identity: callers put this in effect dependency arrays, and a fresh
  // closure each render would either loop the effect or force them to omit it
  // and take an exhaustive-deps warning instead.
  const set = useCallback((next: string) => { setActiveDivision(next); setVal(next); }, []);
  return [val, set];
}

// ---- Divisions available to the caller. Team-scoped server-side, so a HoD
// gets back only their own.
let divisionCache: string[] | null = null;
let divisionPromise: Promise<string[]> | null = null;

export function useDivisions(): string[] {
  const [list, setList] = useState<string[]>(divisionCache ?? []);
  useEffect(() => {
    let alive = true;
    fetchDivisionsOnce().then((v) => { if (alive) setList(v); }).catch(() => {/* silent */});
    return () => { alive = false; };
  }, []);
  return list;
}

/** Shared in-flight fetch — see useCollege for why this is a promise and not
 *  a boolean flag. */
function fetchDivisionsOnce(): Promise<string[]> {
  if (divisionCache) return Promise.resolve(divisionCache);
  if (divisionPromise) return divisionPromise;
  const env = (import.meta as unknown as { env: Record<string, string> }).env;
  const API_BASE = env.VITE_API_URL ?? 'http://localhost:3000';
  const frontendKey = env.VITE_FRONTEND_KEY ?? '';
  const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
  if (!token) return Promise.resolve([]);
  divisionPromise = fetch(`${API_BASE}/api/student/branches`, {
    headers: { ...(frontendKey ? { 'X-Frontend-Key': frontendKey } : {}), Authorization: `Bearer ${token}` },
  })
    .then((r) => (r.ok ? r.json() : null))
    .then((j) => {
      const names: string[] = j && Array.isArray(j.branches) ? j.branches : [];
      divisionCache = names;
      return names;
    })
    .catch(() => [] as string[])
    .finally(() => { divisionPromise = null; });
  return divisionPromise;
}

function DivisionChip() {
  const [active, set] = useActiveDivision();
  const divisions = useDivisions();
  const label = useDivisionLabel();

  // A member restricted to one division has nothing to switch between —
  // render it read-only so the active scope is still visible. This is the
  // HoD case, and it should read as a statement of scope, not a control
  // that appears to offer something it does not.
  useEffect(() => {
    if (divisions.length === 1 && active !== divisions[0]) set(divisions[0]);
  }, [divisions, active, set]);

  if (divisions.length === 0) return null;

  if (divisions.length === 1) {
    return (
      <div
        className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium"
        style={{ background: C.line2, color: C.ink2, border: `1px solid ${C.line}` }}
        title={`Your access is limited to this ${label.toLowerCase()}`}
      >
        <span className="size-1.5 rounded-full" style={{ background: C.accent }} />
        <span style={{ fontFamily: MONO, color: C.ink, maxWidth: 140 }} className="truncate">
          {divisions[0]}
        </span>
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium"
      style={{ background: C.line2, color: C.ink2, border: `1px solid ${C.line}` }}
      title={`Active ${label.toLowerCase()} — chat & reports inherit this scope.`}
    >
      <span className="size-1.5 rounded-full" style={{ background: C.accent }} />
      <select
        value={active}
        onChange={(e) => set(e.target.value)}
        className="bg-transparent cursor-pointer outline-none"
        style={{ fontFamily: MONO, color: C.ink, padding: 0, maxWidth: 140 }}
      >
        <option value="all">{`All ${pluraliseDivision(label).toLowerCase()}`}</option>
        {divisions.map((b) => <option key={b} value={b}>{b}</option>)}
      </select>
    </div>
  );
}

/** Year + institution dropdowns + per-subject pin chips (student /
 *  cohort / company). Use in any page that ships a custom header so
 *  scope controls stay consistent across the app. Year and institution
 *  are the orthogonal broad axes; cohort / student / company are
 *  sticky *subjects* the TPO has zoomed into via @-mention or report
 *  navigation. Multiple pins can be active at once — each is its own
 *  removable amber chip. */
export function ScopeChips() {
  return (
    <>
      <PinnedStudentChip />
      <PinnedCohortChip />
      <PinnedCompanyChip />
      <InstitutionChip />
      <DivisionChip />
      <YearChip />
    </>
  );
}

// ---- Pinned-student scope. When the user picks a student via the
// composer's @-mention picker (or clicks "Pin" on a profile panel), we
// store { id, name } in localStorage and surface a removable chip in
// the topbar. The chat & report layers read this as a hard scope —
// every query is interpreted "about this student" until the chip's X
// clears it.
const PINNED_STUDENT_KEY = 'fb.pinnedStudent';
export type PinnedStudent = { id: string; name: string };

export function getPinnedStudent(): PinnedStudent | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(PINNED_STUDENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PinnedStudent;
    return parsed.id && parsed.name ? parsed : null;
  } catch { return null; }
}
export function setPinnedStudent(s: PinnedStudent | null) {
  if (typeof window === 'undefined') return;
  if (s) window.localStorage.setItem(PINNED_STUDENT_KEY, JSON.stringify(s));
  else   window.localStorage.removeItem(PINNED_STUDENT_KEY);
  window.dispatchEvent(new Event('fb:pinned-student-changed'));
}
export function usePinnedStudent(): [PinnedStudent | null, (s: PinnedStudent | null) => void] {
  const [val, setVal] = useState<PinnedStudent | null>(() => getPinnedStudent());
  useEffect(() => {
    const handler = () => setVal(getPinnedStudent());
    window.addEventListener('fb:pinned-student-changed', handler);
    return () => window.removeEventListener('fb:pinned-student-changed', handler);
  }, []);
  return [val, (next) => { setPinnedStudent(next); setVal(next); }];
}

function PinnedStudentChip() {
  const [pinned, setPin] = usePinnedStudent();
  if (!pinned) return null;
  return <PinChip label={`@${pinned.name}`} title={`Chat & reports stay about ${pinned.name} until you remove this`} onClear={() => setPin(null)} />;
}

// ---- Pinned cohort. Captures id + name + (branch, graduationYear) so
// the server can scope queries to the cohort's branch/year combo when
// the user doesn't override.
const PINNED_COHORT_KEY = 'fb.pinnedCohort';
export type PinnedCohort = { id: string; name: string; branch?: string | null; year?: number | null };

export function getPinnedCohort(): PinnedCohort | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(PINNED_COHORT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PinnedCohort;
    return parsed.id && parsed.name ? parsed : null;
  } catch { return null; }
}
export function setPinnedCohort(c: PinnedCohort | null) {
  if (typeof window === 'undefined') return;
  if (c) window.localStorage.setItem(PINNED_COHORT_KEY, JSON.stringify(c));
  else   window.localStorage.removeItem(PINNED_COHORT_KEY);
  window.dispatchEvent(new Event('fb:pinned-cohort-changed'));
}
export function usePinnedCohort(): [PinnedCohort | null, (c: PinnedCohort | null) => void] {
  const [val, setVal] = useState<PinnedCohort | null>(() => getPinnedCohort());
  useEffect(() => {
    const handler = () => setVal(getPinnedCohort());
    window.addEventListener('fb:pinned-cohort-changed', handler);
    return () => window.removeEventListener('fb:pinned-cohort-changed', handler);
  }, []);
  return [val, (next) => { setPinnedCohort(next); setVal(next); }];
}

function PinnedCohortChip() {
  const [pinned, setPin] = usePinnedCohort();
  if (!pinned) return null;
  const meta = [pinned.branch, pinned.year].filter(Boolean).join(' · ');
  return (
    <PinChip
      label={`Cohort: ${pinned.name}`}
      title={`Chat & reports scope to ${pinned.name}${meta ? ` (${meta})` : ''} until you remove this`}
      onClear={() => setPin(null)}
    />
  );
}

// ---- Pinned company. Captures id + name (no extra metadata needed —
// the server resolves slug/cutoffs at request time).
const PINNED_COMPANY_KEY = 'fb.pinnedCompany';
export type PinnedCompany = { id: string; name: string };

export function getPinnedCompany(): PinnedCompany | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(PINNED_COMPANY_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PinnedCompany;
    return parsed.id && parsed.name ? parsed : null;
  } catch { return null; }
}
export function setPinnedCompany(c: PinnedCompany | null) {
  if (typeof window === 'undefined') return;
  if (c) window.localStorage.setItem(PINNED_COMPANY_KEY, JSON.stringify(c));
  else   window.localStorage.removeItem(PINNED_COMPANY_KEY);
  window.dispatchEvent(new Event('fb:pinned-company-changed'));
}
export function usePinnedCompany(): [PinnedCompany | null, (c: PinnedCompany | null) => void] {
  const [val, setVal] = useState<PinnedCompany | null>(() => getPinnedCompany());
  useEffect(() => {
    const handler = () => setVal(getPinnedCompany());
    window.addEventListener('fb:pinned-company-changed', handler);
    return () => window.removeEventListener('fb:pinned-company-changed', handler);
  }, []);
  return [val, (next) => { setPinnedCompany(next); setVal(next); }];
}

function PinnedCompanyChip() {
  const [pinned, setPin] = usePinnedCompany();
  if (!pinned) return null;
  return (
    <PinChip
      label={`@${pinned.name}`}
      title={`Chat & reports stay about ${pinned.name} until you remove this`}
      onClear={() => setPin(null)}
    />
  );
}

/** Shared amber chip used by every pin variant — keeps the topbar
 *  consistent and lets the per-pin components stay tiny. */
function PinChip({ label, title, onClear }: { label: string; title: string; onClear: () => void }) {
  return (
    <div
      className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium"
      style={{
        background: 'oklch(0.96 0.06 60)',
        color: 'oklch(0.4 0.16 60)',
        border: `1px solid oklch(0.88 0.10 60)`,
      }}
      title={title}
    >
      <span className="text-[10px]">📌</span>
      <span style={{ fontFamily: MONO, color: 'oklch(0.32 0.16 60)', maxWidth: 200 }} className="truncate">
        {label}
      </span>
      <button
        onClick={onClear}
        aria-label="Clear pin"
        className="ml-1 hover:opacity-70 cursor-pointer"
        style={{ color: 'oklch(0.45 0.16 60)', fontSize: 13, lineHeight: 1, background: 'transparent', padding: 0 }}
      >
        ×
      </button>
    </div>
  );
}

function YearChip() {
  const [year, set] = useActiveYear();
  return (
    <div
      className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium"
      style={{ background: C.line2, color: C.ink2, border: `1px solid ${C.line}` }}
      title="Active graduation year — chat assumes this batch when no year is named."
    >
      <span className="size-1.5 rounded-full" style={{ background: C.accent }} />
      <select
        value={String(year)}
        onChange={(e) => set(e.target.value === 'all' ? 'all' : Number(e.target.value))}
        className="bg-transparent cursor-pointer outline-none"
        style={{ fontFamily: MONO, color: C.ink, padding: 0 }}
      >
        <option value="all">All years</option>
        {YEAR_OPTIONS.map((y) => <option key={y} value={y}>{y}</option>)}
      </select>
    </div>
  );
}

// Module-cached institution list per tenant — fetched once via the
// student/picker endpoint.
let institutionCache: string[] | null = null;
let institutionPromise: Promise<string[]> | null = null;

export function useInstitutions(): string[] {
  const [list, setList] = useState<string[]>(institutionCache ?? []);
  useEffect(() => {
    let alive = true;
    fetchInstitutionsOnce().then((v) => { if (alive) setList(v); }).catch(() => {/* silent */});
    return () => { alive = false; };
  }, []);
  return list;
}

/** Shared in-flight fetch — see useCollege for why this is a promise and not
 *  a boolean flag. */
function fetchInstitutionsOnce(): Promise<string[]> {
  if (institutionCache) return Promise.resolve(institutionCache);
  if (institutionPromise) return institutionPromise;
  const env = (import.meta as unknown as { env: Record<string, string> }).env;
  const API_BASE = env.VITE_API_URL ?? 'http://localhost:3000';
  const frontendKey = env.VITE_FRONTEND_KEY ?? '';
  const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
  if (!token) return Promise.resolve([]);
  institutionPromise = fetch(`${API_BASE}/api/student/institutions`, {
    headers: { ...(frontendKey ? { 'X-Frontend-Key': frontendKey } : {}), Authorization: `Bearer ${token}` },
  })
    .then((r) => (r.ok ? r.json() : null))
    .then((j) => {
      const names: string[] = j && Array.isArray(j.institutions) ? j.institutions : [];
      institutionCache = names;
      return names;
    })
    .catch(() => [] as string[])
    .finally(() => { institutionPromise = null; });
  return institutionPromise;
}

function InstitutionChip() {
  const [active, set] = useActiveInstitution();
  const institutions = useInstitutions();
  const college = useCollege();

  // Single-institution tenant — auto-select that one and keep the
  // chip rendered as a read-only label so the user can see the
  // active scope. No "All institutions" since there's nothing to
  // switch between.
  useEffect(() => {
    if (institutions.length === 1 && active !== institutions[0]) {
      set(institutions[0]);
    }
  }, [institutions, active, set]);

  // Second guard, behind the per-tenant keys: a stored campus that this
  // college does not have is dropped rather than left filtering on nothing.
  // The namespaced keys stop it crossing between colleges, but a campus can
  // also be renamed or removed, and a value saved before the namespacing
  // landed is still sitting in someone's browser. Either way the symptom is
  // the same — a roster filtered to a campus that does not exist, showing
  // nobody — so it is worth catching here as well as at the key.
  useEffect(() => {
    if (active !== 'all' && institutions.length > 0 && !institutions.includes(active)) {
      set('all');
    }
  }, [institutions, active, set]);

  // Empty roster (newly-onboarded college, no students yet) —
  // fall back to the college name so the chip is never blank. This
  // gives a TPO who just signed in a clear "you're inside <College>"
  // marker even before they upload any roster data.
  if (institutions.length === 0) {
    if (!college) return null;
    return (
      <div
        className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium"
        style={{ background: C.line2, color: C.ink2, border: `1px solid ${C.line}` }}
        title={college.name}
      >
        <span className="size-1.5 rounded-full" style={{ background: C.accent }} />
        <span style={{ fontFamily: MONO, color: C.ink, maxWidth: 180 }} className="truncate">
          {college.shortCode ?? college.name}
        </span>
      </div>
    );
  }

  if (institutions.length === 1) {
    return (
      <div
        className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium"
        style={{ background: C.line2, color: C.ink2, border: `1px solid ${C.line}` }}
        title="Only campus in this tenant"
      >
        <span className="size-1.5 rounded-full" style={{ background: C.accent }} />
        <span style={{ fontFamily: MONO, color: C.ink, maxWidth: 140 }} className="truncate">
          {institutions[0]}
        </span>
      </div>
    );
  }

  return (
    <div
      className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium"
      style={{ background: C.line2, color: C.ink2, border: `1px solid ${C.line}` }}
      title="Active institution / campus — chat & reports inherit this scope."
    >
      <span className="size-1.5 rounded-full" style={{ background: C.accent }} />
      <select
        value={active}
        onChange={(e) => set(e.target.value)}
        className="bg-transparent cursor-pointer outline-none"
        style={{ fontFamily: MONO, color: C.ink, padding: 0, maxWidth: 140 }}
      >
        <option value="all">All institutions</option>
        {institutions.map((i) => <option key={i} value={i}>{i}</option>)}
      </select>
    </div>
  );
}

// ---- College info hook + tiny in-memory cache so we hit /api/college/me once
// per session, not once per page navigation.
type CollegeInfo = { name: string; shortCode: string | null; divisionLabel: string };
let collegeCache: CollegeInfo | null = null;
let collegePromise: Promise<CollegeInfo | null> | null = null;

/**
 * The caller's college, fetched once per session and shared.
 *
 * The guard here is a PROMISE, not a boolean. With a boolean, the first hook
 * instance to run its effect set the flag and every other instance returned
 * early — so only the winner ever received the value, and any sibling mounted
 * in the same tick stayed null for the rest of the session. That was invisible
 * while useCollege had one consumer; it surfaced the moment the division chip
 * became a second one, mounted alongside InstitutionChip.
 *
 * The old flag was also set before the token check, so a call with no token
 * poisoned the cache permanently.
 */
export function useCollege(): CollegeInfo | null {
  const [college, setCollege] = useState<CollegeInfo | null>(collegeCache);
  useEffect(() => {
    let alive = true;
    fetchCollegeOnce()
      .then((info) => { if (alive && info) setCollege(info); })
      .catch(() => {/* silent — chip just won't render */});
    return () => { alive = false; };
  }, []);
  return college;
}

function fetchCollegeOnce(): Promise<CollegeInfo | null> {
  if (collegeCache) return Promise.resolve(collegeCache);
  if (collegePromise) return collegePromise;
  const env = (import.meta as unknown as { env: Record<string, string> }).env;
  const API_BASE = env.VITE_API_URL ?? 'http://localhost:3000';
  const frontendKey = env.VITE_FRONTEND_KEY ?? '';
  const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
  // No token yet: resolve null WITHOUT memoising, so a later call retries.
  if (!token) return Promise.resolve(null);
  collegePromise = fetch(`${API_BASE}/api/college/me`, {
    headers: {
      ...(frontendKey ? { 'X-Frontend-Key': frontendKey } : {}),
      Authorization: `Bearer ${token}`,
    },
  })
    .then((r) => (r.ok ? r.json() : null))
    .then((c) => {
      if (!c) return null;
      const info: CollegeInfo = {
        name: c.name,
        shortCode: c.shortCode ?? null,
        // Server resolves college override -> DIVISION_LABEL -> 'Branch'.
        divisionLabel: c.divisionLabel || 'Branch',
      };
      collegeCache = info;
      return info;
    })
    .catch(() => null)
    .finally(() => { collegePromise = null; });
  return collegePromise;
}

export const APP_C = C;
export const APP_MONO = MONO;
